import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  ColumnLayout,
  Box,
  Badge,
  KeyValuePairs,
  ExpandableSection,
  Alert,
  Button,
  Modal,
  Textarea
} from '@cloudscape-design/components';

function DocumentViewer({ document }) {
  const [showRawData, setShowRawData] = useState(false);

  if (!document) {
    return (
      <Container>
        <Box textAlign="center" color="inherit">
          <b>No document selected</b>
          <Box padding={{ bottom: 's' }} variant="p" color="inherit">
            Select a document from the list to view its details.
          </Box>
        </Box>
      </Container>
    );
  }

  const getStatusBadge = (isComplete) => {
    if (isComplete === true) {
      return <Badge color="success">Form Complete</Badge>;
    } else if (isComplete === false) {
      return <Badge color="warning">Incomplete Form</Badge>;
    }
    return <Badge color="blue">Processing</Badge>;
  };

  const formatFields = (fields) => {
    if (!fields || typeof fields !== 'object') return [];
    
    return Object.entries(fields)
      .map(([key, value]) => ({
        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
        value: value || 'Not provided',
        originalKey: key
      }))
      .sort((a, b) => {
        // Extract numbers from field names for sorting
        const aMatch = a.originalKey.match(/\d+/);
        const bMatch = b.originalKey.match(/\d+/);
        
        if (aMatch && bMatch) {
          const aNum = parseInt(aMatch[0]);
          const bNum = parseInt(bMatch[0]);
          return aNum - bNum;
        }
        
        // If no numbers, sort alphabetically
        return a.originalKey.localeCompare(b.originalKey);
      });
  };

  const documentInfo = [
    { label: 'File Name', value: document.fileKey?.split('/').pop() || 'Unknown' },
    { label: 'File Type', value: document.fileType?.toUpperCase() || 'Unknown' },
    { label: 'Upload Time', value: new Date(document.uploadTimestamp).toLocaleString() },
    { label: 'Confidence Score', value: document.confidence ? `${Math.round(document.confidence)}%` : 'N/A' },
    { label: 'Status', value: getStatusBadge(document.isFormComplete) }
  ];

  const getConfidenceStatusColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 75) return 'info';
    return 'warning';
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Document processing results and extracted data"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="normal"
                onClick={() => setShowRawData(true)}
              >
                View Raw Data
              </Button>
            </SpaceBetween>
          }
        >
          Document Details
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <ColumnLayout columns={2} variant="text-grid">
          <div>
            <Box variant="h3">Document Information</Box>
            <KeyValuePairs columns={1} items={documentInfo} />
          </div>
          
          <div>
            <Box variant="h3">Processing Status</Box>
            <SpaceBetween direction="vertical" size="s">
              {document.isFormComplete === true && (
                <Alert type="success">
                  Document processing completed successfully. All required fields were extracted.
                </Alert>
              )}
              {document.isFormComplete === false && (
                <Alert type="warning">
                  Document processing completed but some required fields may be missing or have low confidence scores.
                </Alert>
              )}
              {document.isFormComplete === undefined && (
                <Alert type="info">
                  Document is still being processed. Please check back later.
                </Alert>
              )}
            </SpaceBetween>
          </div>
        </ColumnLayout>

        {document.confidenceAnalysis && (
          <Container
            header={
              <Header 
                variant="h3"
                description="Document extraction quality analysis"
              >
                <SpaceBetween direction="horizontal" size="s" alignItems="center">
                  <span>Confidence Score Analysis - Powered by</span>
                  <div style={{ textAlign: 'center', display: 'inline-block' }}>
                    <img 
                      src={require('../assets/textractlogo.png')} 
                      alt="Amazon Textract" 
                      style={{ height: '40px', display: 'block', margin: '0 auto' }}
                    />
                    <Box fontSize="body-s" color="text-body-secondary" padding={{ top: 'xxxs' }} textAlign="center">
                      Amazon Textract
                    </Box>
                  </div>
                </SpaceBetween>
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="m">
              <Alert type={getConfidenceStatusColor(document.confidence)}>
                <Box variant="strong">Overall Confidence: {Math.round(document.confidence)}%</Box>
                <Box variant="p" padding={{ top: 'xs' }}>
                  {document.confidenceAnalysis.summary}
                </Box>
              </Alert>

              {document.confidenceAnalysis.factors && document.confidenceAnalysis.factors.length > 0 && (
                <div>
                  <Box variant="h4" padding={{ bottom: 's' }}>Score Breakdown</Box>
                  <ColumnLayout columns={3} variant="text-grid">
                    {document.confidenceAnalysis.factors.map((factor, idx) => (
                      <div key={idx}>
                        <Box variant="awsui-key-label">{factor.category}</Box>
                        <Box fontSize="heading-l" fontWeight="bold" padding={{ top: 'xxxs', bottom: 'xxxs' }}>
                          {factor.score}%
                        </Box>
                        <Box fontSize="body-s" color="text-body-secondary">
                          {factor.description}
                        </Box>
                      </div>
                    ))}
                  </ColumnLayout>
                </div>
              )}

              {document.confidenceAnalysis.recommendations && document.confidenceAnalysis.recommendations.length > 0 && (
                <div>
                  <Box variant="h4" padding={{ bottom: 's' }}>Recommendations</Box>
                  <SpaceBetween direction="vertical" size="xs">
                    {document.confidenceAnalysis.recommendations.map((rec, idx) => (
                      <Box key={idx} fontSize="body-m">
                        <Badge color="red">!</Badge> {rec}
                      </Box>
                    ))}
                  </SpaceBetween>
                </div>
              )}
            </SpaceBetween>
          </Container>
        )}

        {/* Bedrock AI Analysis Section */}
        <Container
          header={
            <Header 
              variant="h3"
              description="Advanced AI-powered validation"
            >
              <SpaceBetween direction="horizontal" size="s" alignItems="center">
                <span>AI Validation - Powered by</span>
                <div style={{ textAlign: 'center', display: 'inline-block' }}>
                  <img 
                    src={require('../assets/bedrock.png')} 
                    alt="Amazon Bedrock" 
                    style={{ height: '40px', display: 'block', margin: '0 auto' }}
                  />
                  <Box fontSize="body-s" color="text-body-secondary" padding={{ top: 'xxxs' }} textAlign="center">
                    Amazon Bedrock
                  </Box>
                </div>
              </SpaceBetween>
            </Header>
          }
        >
          {document.bedrockAnalysis ? (
            <ExpandableSection
              headerText="AI Validation Analysis"
              variant="footer"
              defaultExpanded={true}
              headerActions={
                <Badge color={
                  document.bedrockAnalysis.complianceStatus === 'COMPLIANT' ? 'green' :
                  document.bedrockAnalysis.complianceStatus === 'NON_COMPLIANT' ? 'red' :
                  'blue'
                }>
                  {document.bedrockAnalysis.complianceStatus === 'COMPLIANT' ? 'COMPLIANT' :
                   document.bedrockAnalysis.complianceStatus === 'NON_COMPLIANT' ? 'NON-COMPLIANT' :
                   'REQUIRES REVIEW'}
                </Badge>
              }
            >
              <SpaceBetween direction="vertical" size="m">
                {/* AI Summary */}
                <Container>
                  <SpaceBetween direction="vertical" size="s">
                    <Box variant="awsui-key-label">AI Summary</Box>
                    <Box variant="p">{document.bedrockAnalysis.summary}</Box>
                    <Box variant="small" color="text-status-inactive">
                      Confidence: {document.bedrockAnalysis.confidence}% • 
                      Analyzed: {new Date(document.bedrockAnalysis.timestamp).toLocaleString()}
                    </Box>
                  </SpaceBetween>
                </Container>

                {/* Critical Issues */}
                {document.bedrockAnalysis.criticalIssues && document.bedrockAnalysis.criticalIssues.length > 0 && (
                  <Alert type="error" header={`Critical Issues (${document.bedrockAnalysis.criticalIssues.length})`}>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {document.bedrockAnalysis.criticalIssues.map((issue, idx) => (
                        <li key={idx} style={{ marginBottom: '8px' }}>{issue}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                {/* Recommendations */}
                {document.bedrockAnalysis.recommendations && document.bedrockAnalysis.recommendations.length > 0 && (
                  <Alert type="info" header={`Recommendations (${document.bedrockAnalysis.recommendations.length})`}>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {document.bedrockAnalysis.recommendations.map((rec, idx) => (
                        <li key={idx} style={{ marginBottom: '8px' }}>{rec}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                {/* Full Analysis */}
                <ExpandableSection headerText="View Full AI Analysis" variant="footer">
                  <Container>
                    <Box variant="pre" fontSize="body-s">
                      {document.bedrockAnalysis.fullAnalysis}
                    </Box>
                  </Container>
                </ExpandableSection>
              </SpaceBetween>
            </ExpandableSection>
          ) : (
            <Alert type="info">
              <Box variant="strong">Coming Soon</Box>
              <Box variant="p" padding={{ top: 'xs' }}>
                AI-powered validation using Amazon Bedrock will provide:
              </Box>
              <Box padding={{ top: 's', left: 'm' }}>
                <SpaceBetween direction="vertical" size="xs">
                  <Box>• Intelligent content analysis and context understanding</Box>
                  <Box>• Natural language validation of narrative sections</Box>
                  <Box>• Anomaly detection and consistency checks</Box>
                  <Box>• Automated quality recommendations</Box>
                  <Box>• Enhanced accuracy beyond rule-based validation</Box>
                </SpaceBetween>
              </Box>
            </Alert>
          )}
        </Container>

        {/* Business Rules Analysis Section */}
        {document.businessRulesScore !== undefined && (
          <ExpandableSection
            headerText="Business Rules Analysis"
            variant="container"
            defaultExpanded={true}
            headerDescription="Validation against Navy personnel evaluation standards"
          >
            <SpaceBetween direction="vertical" size="m">
              {/* Overall Score */}
              <Alert 
                type={
                  document.businessRulesScore === 100 ? 'success' :
                  document.businessRulesScore >= 90 ? 'info' :
                  document.businessRulesScore >= 75 ? 'warning' : 'error'
                }
              >
                <Box variant="strong">
                  Business Rules Score: {Math.round(document.businessRulesScore)}%
                </Box>
                <Box variant="p" padding={{ top: 'xs' }}>
                  {document.businessRulesScore === 100 
                    ? 'All business rules passed. Form is compliant with Navy standards.'
                    : document.businessRulesScore >= 90
                    ? 'Minor violations detected. Review recommended.'
                    : document.businessRulesScore >= 75
                    ? 'Multiple violations detected. Manual review required.'
                    : 'Critical violations detected. Immediate review required.'}
                </Box>
                {document.requiresQC && (
                  <Box padding={{ top: 's' }}>
                    <Badge color="red">Quality Control Required</Badge>
                  </Box>
                )}
              </Alert>

              {/* Validation Summary */}
              {document.businessRulesValidatedAt && (
                <ColumnLayout columns={3} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Validated At</Box>
                    <Box fontSize="body-m" padding={{ top: 'xxxs' }}>
                      {new Date(document.businessRulesValidatedAt).toLocaleString()}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Violations Found</Box>
                    <Box fontSize="heading-l" fontWeight="bold" padding={{ top: 'xxxs' }}>
                      {document.ruleViolations ? document.ruleViolations.length : 0}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">QC Status</Box>
                    <Box padding={{ top: 'xxxs' }}>
                      {document.requiresQC ? (
                        <Badge color="red">Required</Badge>
                      ) : (
                        <Badge color="green">Not Required</Badge>
                      )}
                    </Box>
                  </div>
                </ColumnLayout>
              )}

              {/* Violations List */}
              {document.ruleViolations && document.ruleViolations.length > 0 && (
                <div>
                  <Box variant="h4" padding={{ bottom: 's' }}>
                    Rule Violations ({document.ruleViolations.length})
                  </Box>
                  <SpaceBetween direction="vertical" size="s">
                    {document.ruleViolations
                      .sort((a, b) => {
                        // Sort by rule number numerically
                        const aNum = parseInt(a.ruleNumber || a.ruleId.replace(/\D/g, '')) || 0;
                        const bNum = parseInt(b.ruleNumber || b.ruleId.replace(/\D/g, '')) || 0;
                        return aNum - bNum;
                      })
                      .map((violation, idx) => (
                      <Container key={idx}>
                        <SpaceBetween direction="vertical" size="xs">
                          <Box>
                            <SpaceBetween direction="horizontal" size="xs">
                              <Badge color="red">{violation.ruleId}</Badge>
                              <Badge color={
                                violation.severity === 'CRITICAL' ? 'red' :
                                violation.severity === 'HIGH' ? 'red' :
                                violation.severity === 'MEDIUM' ? 'blue' : 'grey'
                              }>
                                {violation.severity}
                              </Badge>
                              {violation.autoQC && <Badge color="red">Auto QC</Badge>}
                              {violation.block && (
                                <Badge color="grey">Block {violation.block}</Badge>
                              )}
                            </SpaceBetween>
                          </Box>
                          <Box fontSize="heading-s" fontWeight="bold">
                            {violation.title}
                          </Box>
                          <Box fontSize="body-s" color="text-status-error">
                            {violation.message}
                          </Box>
                          {violation.details && (
                            <Box padding={{ top: 'xs' }}>
                              <Box fontSize="body-s" fontWeight="bold" color="text-body-secondary">
                                Details:
                              </Box>
                              {violation.details.formValue && violation.details.databaseValue && (
                                <ColumnLayout columns={2} variant="text-grid">
                                  <div>
                                    <Box fontSize="body-s" color="text-body-secondary">Form Value:</Box>
                                    <Box fontSize="body-s" fontWeight="bold" color="text-status-error">
                                      {violation.details.formValue}
                                    </Box>
                                  </div>
                                  <div>
                                    <Box fontSize="body-s" color="text-body-secondary">Expected (OMPF):</Box>
                                    <Box fontSize="body-s" fontWeight="bold" color="text-status-success">
                                      {violation.details.databaseValue}
                                    </Box>
                                  </div>
                                </ColumnLayout>
                              )}
                              {violation.details.source && (
                                <Box fontSize="body-s" color="text-body-secondary" padding={{ top: 'xxs' }}>
                                  Source: {violation.details.source}
                                </Box>
                              )}
                              {violation.details.reason && (
                                <Box fontSize="body-s" color="text-body-secondary" padding={{ top: 'xxs' }}>
                                  Reason: {violation.details.reason}
                                </Box>
                              )}
                            </Box>
                          )}
                        </SpaceBetween>
                      </Container>
                    ))}
                  </SpaceBetween>
                </div>
              )}

              {/* No Violations Message */}
              {(!document.ruleViolations || document.ruleViolations.length === 0) && (
                <Alert type="success">
                  <Box variant="strong">No Violations Found</Box>
                  <Box variant="p">
                    This form complies with all applicable Navy personnel evaluation business rules.
                  </Box>
                </Alert>
              )}
            </SpaceBetween>
          </ExpandableSection>
        )}

        {/* Show message if business rules haven't been validated yet */}
        {document.businessRulesScore === undefined && document.processingStatus === 'COMPLETE' && (
          <Alert type="info">
            <Box variant="strong">Business Rules Validation Pending</Box>
            <Box variant="p">
              This document has been processed by Textract but business rules validation is still in progress. 
              Please refresh in a few moments.
            </Box>
          </Alert>
        )}



        {document.extractedText && (
          <ExpandableSection headerText="Extracted Text" variant="container">
            <Box variant="code">
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {document.extractedText}
              </pre>
            </Box>
          </ExpandableSection>
        )}

        {document.formFields && Object.keys(document.formFields).length > 0 && (
          <ExpandableSection headerText="Extracted Form Fields" variant="container" defaultExpanded>
            <KeyValuePairs
              columns={2}
              items={formatFields(document.formFields)}
            />
          </ExpandableSection>
        )}

        {document.confidence && document.confidence < 80 && (
          <Alert type="warning">
            <Box variant="strong">Low Confidence Warning</Box>
            <Box variant="p">
              This document has a confidence score below 80%. Please review the extracted data carefully.
            </Box>
          </Alert>
        )}

        <Modal
          visible={showRawData}
          onDismiss={() => setShowRawData(false)}
          header="Raw Document Data"
          size="large"
        >
          <SpaceBetween direction="vertical" size="m">
            <Box variant="p">
              This is the raw data stored in DynamoDB for this document.
            </Box>
            <Textarea
              value={JSON.stringify(document, null, 2)}
              rows={20}
              readOnly
            />
          </SpaceBetween>
        </Modal>
      </SpaceBetween>
    </Container>
  );
}

export default DocumentViewer;